import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Ban, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { ReasonDialog } from "@/components/BookingStatus";
import { api, errorText } from "@/lib/api";
import { fmtDateTime, fmtDay } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";

export type RegKind = "partners" | "customers";
export type RegRow = { id: string; name: string; business_name?: string; username?: string; email: string; phone?: string; status?: string; disabled: boolean; created_at?: string; google?: boolean; blockers: Record<string, number> };
type Items = { places?: any[]; events?: any[]; bookings?: any[] };

const itemBtn = "h-8 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 bg-primary/10 text-primary active:scale-95 transition-transform shrink-0";

const ItemRow = ({ id, title, sub, action, onClick }: { id: string; title: string; sub: string; action?: string; onClick?: () => void }) => (
  <li data-testid={id} className="flex items-center justify-between gap-2 rounded-2xl bg-muted/60 px-3 py-2">
    <div className="min-w-0"><p className="text-sm font-bold truncate">{title}</p><p className="text-xs text-muted-foreground">{sub}</p></div>
    {action && <button onClick={onClick} data-testid={`${id}-action`} className={itemBtn}>{action}</button>}
  </li>
);

export const AccountDeleteDialog = ({ kind, row, onClose, onDone }: { kind: RegKind; row: RegRow; onClose: () => void; onDone: () => void }) => {
  const { t, lang } = usePrefs();
  const [items, setItems] = useState<Items | null>(null);
  const [cancel, setCancel] = useState<{ path: string; title: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/registry/${kind}/${row.id}/items`).then((r) => setItems(r.data)).catch(() => setItems({}));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); toast.success(t(ok)); } catch (e) { toast.error(errorText(e, t)); }
  };
  const blocked = !!items && !!((items.places?.length || 0) + (items.events?.length || 0) + (items.bookings?.length || 0));
  const del = async () => {
    setBusy(true);
    await run(() => api.delete(`/admin/registry/${kind}/${row.id}`), "reg.deleted");
    setBusy(false);
    onDone();
  };
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="account-delete-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-display break-words pe-8 leading-snug">{t("reg.deleteTitle").replace("{name}", row.business_name || row.name)}</DialogTitle></DialogHeader>
        {!items ? <div className="h-20 rounded-2xl bg-muted animate-pulse" /> : blocked ? (
          <div className="space-y-3">
            <p className="rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 px-3 py-2 text-sm font-semibold flex gap-2" data-testid="account-delete-blocked">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{t(kind === "partners" ? "reg.blockedPartner" : "reg.blockedCustomer")}
            </p>
            {!!items.events?.length && <><p className="text-xs font-bold">{t("reg.upcomingEvents")}</p><ul className="space-y-1.5">
              {items.events.map((e) => <ItemRow key={e.event_id} id={`reg-event-${e.event_id}`} title={e.title_ar} sub={`${fmtDateTime(e.start_at, lang)} · ${t("reg.activeBookings").replace("{n}", e.active_bookings)}`}
                action={t("reg.cancelEvent")} onClick={() => setCancel({ path: `/admin/registry/events/${e.event_id}/cancel`, title: t("reg.cancelEvent") })} />)}
            </ul></>}
            {!!items.places?.length && <><p className="text-xs font-bold">{t("reg.places")}</p><ul className="space-y-1.5">
              {items.places.map((v) => <ItemRow key={v.venue_id} id={`reg-place-${v.venue_id}`} title={v.name_ar} sub={v.upcoming_events ? t("reg.placeHasEvents").replace("{n}", v.upcoming_events) : t("reg.placeFree")}
                action={v.upcoming_events ? undefined : t("reg.deletePlace")} onClick={() => run(() => api.delete(`/admin/registry/places/${v.venue_id}`), "reg.placeDeleted").then(load)} />)}
            </ul></>}
            {!!items.bookings?.length && <><p className="text-xs font-bold">{t("reg.activeBookingsTitle")}</p><ul className="space-y-1.5">
              {items.bookings.map((b) => <ItemRow key={b.booking_id} id={`reg-booking-${b.booking_id}`} title={b.event_title_ar} sub={`${b.venue_name_ar} · ${fmtDay(b.date, lang)} · ${b.guests}`}
                action={t("reg.cancelBooking")} onClick={() => setCancel({ path: `/admin/registry/bookings/${b.booking_id}/cancel`, title: t("reg.cancelBooking") })} />)}
            </ul></>}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("reg.deleteWarn")}</p>
            <button onClick={del} disabled={busy} data-testid="account-delete-confirm" className="w-full h-11 rounded-full bg-primary text-primary-foreground font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Trash2 className="h-4 w-4" />{t("reg.deleteForever")}
            </button>
          </div>
        )}
        <button onClick={onClose} data-testid="account-delete-close" className="w-full h-11 rounded-full bg-accent font-bold text-sm inline-flex items-center justify-center gap-2"><Ban className="h-4 w-4" />{t("common.close")}</button>
        {cancel && <ReasonDialog testid="reg-cancel-dialog" title={cancel.title} onClose={() => setCancel(null)}
          onSubmit={(reason) => run(() => api.post(cancel.path, { reason }), "reg.cancelled").then(() => { setCancel(null); load(); })} />}
      </DialogContent>
    </Dialog>
  );
};
